/**
 * Zod schemas for the template-tree authoring shape consumed by the Wave 1
 * template editor (Plan 03's RHF form). Mirrors the backend `TemplateRequest`
 * shape from `components["schemas"]["CreateTemplateRequest"]` /
 * `UpdateTemplateRequest` exactly — both POST (create) and PUT (replace) send
 * the same tree.
 *
 * **Bilingual invariants:** every named entity (template, section, option)
 * requires BOTH `nameEn`/`nameAr` (or `labelEn`/`labelAr`, or `textEn`/`textAr`)
 * to be non-empty. This is the "no half-translated template" gate (D-03).
 *
 * **Tree invariants:**
 *   - template: ≥ 1 section
 *   - section:  ≥ 1 question
 *   - CHOICE_* questions: ≥ 2 options (single-option choices are nonsense)
 *
 * IDs (`id`) are optional on every level so the editor can author new
 * children without a server round-trip — backend mints UUIDs on PUT response.
 */
import { z } from "zod";
import { QUESTION_TYPES } from "./questionTypes";

const uuidOptional = z.string().uuid().optional();

export const optionRequestSchema = z.object({
  id: uuidOptional,
  labelEn: z.string().min(1),
  labelAr: z.string().min(1),
  displayOrder: z.number().int(),
});
export type OptionRequest = z.infer<typeof optionRequestSchema>;

export const questionRequestSchema = z
  .object({
    id: uuidOptional,
    textEn: z.string().min(1),
    textAr: z.string().min(1),
    questionType: z.enum(QUESTION_TYPES),
    required: z.boolean(),
    weight: z.number().min(0),
    displayOrder: z.number().int(),
    options: z.array(optionRequestSchema).optional(),
  })
  .refine(
    (q) =>
      q.questionType === "CHOICE_SINGLE" || q.questionType === "CHOICE_MULTI"
        ? (q.options?.length ?? 0) >= 2
        : true,
    {
      message: "CHOICE_* questions require at least 2 options",
      path: ["options"],
    },
  );
export type QuestionRequest = z.infer<typeof questionRequestSchema>;

export const sectionRequestSchema = z.object({
  id: uuidOptional,
  nameEn: z.string().min(1),
  nameAr: z.string().min(1),
  displayOrder: z.number().int(),
  weight: z.number().min(0),
  questions: z.array(questionRequestSchema).min(1),
});
export type SectionRequest = z.infer<typeof sectionRequestSchema>;

export const templateRequestSchema = z.object({
  nameEn: z.string().min(1),
  nameAr: z.string().min(1),
  descriptionEn: z.string().optional(),
  descriptionAr: z.string().optional(),
  sections: z.array(sectionRequestSchema).min(1),
});
export type TemplateRequest = z.infer<typeof templateRequestSchema>;
